// ==================== CANCELLATION POLICY PAGE COMPONENT WITH EDIT FORM ====================

import React, { useEffect, useState } from "react";
import { getData, postData, serverURL } from "../../../Services/Fetchnodeservices";

const CancellationPolicy = ({ isDark }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    page_title: "",
    page_description: "",
    page_thumbnail: null,
  });
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [updating, setUpdating] = useState(false);

  const t = isDark
    ? {
        bg: "#0a0c10",
        cardBg: "#141824",
        text: "#e2e8f0",
        textSecondary: "#94a3b8",
        border: "rgba(255,255,255,0.08)",
        heading: "#ffffff",
        code: "#1e293b",
        inputBg: "#1e293b",
      }
    : {
        bg: "#f8fafc",
        cardBg: "#ffffff",
        text: "#334155",
        textSecondary: "#64748b",
        border: "rgba(0,0,0,0.1)",
        heading: "#0f172a",
        code: "#f1f5f9",
        inputBg: "#ffffff",
      };

  const fetchPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("Apppages/fetchapppagecancellationpolicy");
      console.log("API Response:", response);

      if (response?.status && response?.data) {
        setPage(response.data);
        setEditForm({
          page_title: response.data.page_title || "",
          page_description: response.data.page_description || "",
          page_thumbnail: null,
        });
      } else {
        setError("Cancellation Policy not found");
      }
    } catch (e) {
      console.error("Error fetching cancellation policy:", e);
      setError("Failed to load cancellation policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditForm({
      page_title: page?.page_title || "",
      page_description: page?.page_description || "",
      page_thumbnail: null,
    });
    setThumbnailPreview(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      page_title: page?.page_title || "",
      page_description: page?.page_description || "",
      page_thumbnail: null,
    });
    setThumbnailPreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm(prev => ({ ...prev, page_thumbnail: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const formData = new FormData();
      formData.append("id", page.id);
      formData.append("page_title", editForm.page_title);
      formData.append("page_description", editForm.page_description);
      if (editForm.page_thumbnail) {
        formData.append("page_thumbnail", editForm.page_thumbnail);
      }

      const response = await postData("cancellationpolicy/update_cancellation_policy", formData, true);
      
      if (response?.status) {
        await fetchPage();
        setIsEditing(false);
        alert("Cancellation Policy updated successfully!");
      } else {
        alert("Failed to update. Please try again.");
      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Error updating cancellation policy");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: t.bg,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "50px",
              height: "50px",
              border: `3px solid ${t.border}`,
              borderTopColor: "#3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px",
            }}
          />
          <p style={{ color: t.textSecondary }}>Loading cancellation policy...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: t.bg,
          padding: "20px",
        }}
      >
        <div
          style={{
            textAlign: "center",
            backgroundColor: t.cardBg,
            padding: "40px",
            borderRadius: "16px",
            border: `1px solid ${t.border}`,
            maxWidth: "400px",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔄</div>
          <h2 style={{ color: t.heading, marginBottom: "8px" }}>Cancellation Policy Not Found</h2>
          <p style={{ color: t.textSecondary }}>{error}</p>
          <button
            onClick={fetchPage}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .cancellation-policy-content {
          word-wrap: break-word;
        }
        .cancellation-policy-content h1,
        .cancellation-policy-content h2,
        .cancellation-policy-content h3,
        .cancellation-policy-content h4 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }
        .cancellation-policy-content h1 {
          font-size: 2rem;
          margin-top: 0;
          color: ${t.heading};
        }
        .cancellation-policy-content h2 {
          font-size: 1.5rem;
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          color: ${t.heading};
        }
        .cancellation-policy-content h3 {
          font-size: 1.25rem;
          color: ${t.heading};
        }
        .cancellation-policy-content h4 {
          font-size: 1.1rem;
          color: ${t.text};
        }
        .cancellation-policy-content p {
          line-height: 1.7;
          margin-bottom: 1rem;
          color: ${t.text};
        }
        .cancellation-policy-content ul,
        .cancellation-policy-content ol {
          margin: 1rem 0;
          padding-left: 2rem;
          color: ${t.text};
        }
        .cancellation-policy-content li {
          margin: 0.5rem 0;
          line-height: 1.6;
        }
        .cancellation-policy-content hr {
          margin: 2rem 0;
          border: none;
          height: 1px;
          background: ${t.border};
        }
        .cancellation-policy-content blockquote {
          margin: 1rem 0;
          padding: 1rem 1.5rem;
          background: ${t.code};
          border-left: 4px solid #3b82f6;
          border-radius: 8px;
          font-style: italic;
          color: ${t.textSecondary};
        }
        .cancellation-policy-content a {
          color: #3b82f6;
          text-decoration: none;
        }
        .cancellation-policy-content a:hover {
          text-decoration: underline;
        }
        .cancellation-policy-content strong {
          color: ${t.heading};
          font-weight: 600;
        }
        .cancellation-policy-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        .cancellation-policy-content th,
        .cancellation-policy-content td {
          border: 1px solid ${t.border};
          padding: 0.75rem;
          text-align: left;
          color: ${t.text};
        }
        .cancellation-policy-content th {
          background: ${t.code};
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .cancellation-policy-content h1 {
            font-size: 1.5rem;
          }
          .cancellation-policy-content h2 {
            font-size: 1.25rem;
          }
          .cancellation-policy-content h3 {
            font-size: 1.1rem;
          }
        }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          backgroundColor: t.bg,
          padding: "40px 20px",
        }}
      >
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            backgroundColor: t.cardBg,
            borderRadius: "20px",
            border: `1px solid ${t.border}`,
            overflow: "hidden",
            boxShadow: isDark
              ? "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Edit Button */}
          <div
            style={{
              padding: "20px 48px",
              display: "flex",
              justifyContent: "flex-end",
              borderBottom: `1px solid ${t.border}`,
              backgroundColor: t.bg,
            }}
          >
            {!isEditing ? (
              <button
                onClick={handleEditClick}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "10px 20px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                ✏️ Edit Cancellation Policy
              </button>
            ) : (
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={handleCancelEdit}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "transparent",
                    color: t.text,
                    border: `1px solid ${t.border}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={updating}
                  style={{
                    padding: "10px 20px",
                    backgroundColor: "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: updating ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    opacity: updating ? 0.6 : 1,
                  }}
                >
                  {updating ? "Saving..." : "💾 Save Changes"}
                </button>
              </div>
            )}
          </div>

          {/* Edit Form */}
          {isEditing && (
            <div
              style={{
                padding: "32px 48px",
                backgroundColor: t.code,
                borderBottom: `1px solid ${t.border}`,
              }}
            >
              <h3 style={{ color: t.heading, marginBottom: "20px", fontSize: "18px" }}>
                Edit Cancellation Policy
              </h3>
              
              {/* Title Input */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ color: t.text, display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Page Title
                </label>
                <input
                  type="text"
                  name="page_title"
                  value={editForm.page_title}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: t.inputBg,
                    border: `1px solid ${t.border}`,
                    borderRadius: "8px",
                    color: t.text,
                    fontSize: "16px",
                  }}
                />
              </div>

              {/* Thumbnail Upload */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ color: t.text, display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Thumbnail Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: t.inputBg,
                    border: `1px solid ${t.border}`,
                    borderRadius: "8px",
                    color: t.text,
                  }}
                />
                {(thumbnailPreview || page?.page_thumbnail) && (
                  <div style={{ marginTop: "12px" }}>
                    <p style={{ color: t.textSecondary, fontSize: "12px", marginBottom: "8px" }}>Preview:</p>
                    <img
                      src={thumbnailPreview || `${serverURL}/images/${page.page_thumbnail}`}
                      alt="Thumbnail preview"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: `1px solid ${t.border}`,
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Description Textarea */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ color: t.text, display: "block", marginBottom: "8px", fontWeight: "500" }}>
                  Page Content (HTML supported)
                </label>
                <textarea
                  name="page_description"
                  value={editForm.page_description}
                  onChange={handleInputChange}
                  rows={15}
                  style={{
                    width: "100%",
                    padding: "12px",
                    backgroundColor: t.inputBg,
                    border: `1px solid ${t.border}`,
                    borderRadius: "8px",
                    color: t.text,
                    fontSize: "14px",
                    fontFamily: "monospace",
                  }}
                />
              </div>
            </div>
          )}

          {/* Thumbnail Image */}
          {!isEditing && page?.page_thumbnail && (
            <div
              style={{
                position: "relative",
                height: "300px",
                overflow: "hidden",
              }}
            >
              <img
                src={`${serverURL}/images/${page.page_thumbnail}`}
                alt={page.page_title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentElement.style.height = "auto";
                }}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
                  padding: "40px 40px 20px",
                }}
              >
                <h1
                  style={{
                    color: "white",
                    fontSize: "2.5rem",
                    margin: 0,
                    fontWeight: "700",
                  }}
                >
                  {page?.page_title}
                </h1>
                <p
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    marginTop: "8px",
                    fontSize: "14px",
                  }}
                >
                  Last updated: {formatDate(page?.updated_at)}
                </p>
              </div>
            </div>
          )}

          {/* No Thumbnail - Show Gradient Header */}
          {!isEditing && !page?.page_thumbnail && (
            <div
              style={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                padding: "60px 40px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  fontSize: "40px",
                }}
              >
                🔄
              </div>
              <h1 style={{ color: "white", margin: 0, fontSize: "2.5rem" }}>
                {page?.page_title}
              </h1>
              <p style={{ color: "rgba(255,255,255,0.8)", marginTop: "8px" }}>
                Last updated: {formatDate(page?.updated_at)}
              </p>
            </div>
          )}

          {/* Content Section */}
          <div style={{ padding: "48px" }}>
            {!isEditing ? (
              <div
                className="cancellation-policy-content"
                dangerouslySetInnerHTML={{ __html: page?.page_description || "No content available" }}
              />
            ) : (
              <div
                style={{
                  padding: "20px",
                  backgroundColor: t.code,
                  borderRadius: "12px",
                  textAlign: "center",
                  color: t.textSecondary,
                }}
              >
                ⚡ Edit mode active - Make your changes in the form above and click Save
              </div>
            )}

            {/* Footer Information */}
            {!isEditing && (
              <div
                style={{
                  marginTop: "48px",
                  paddingTop: "24px",
                  borderTop: `1px solid ${t.border}`,
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "16px",
                  fontSize: "13px",
                  color: t.textSecondary,
                }}
              >
                <div>
                  <strong>📄 Page ID:</strong> {page?.id}
                </div>
                <div>
                  <strong>📅 Created:</strong> {formatDate(page?.created_at)}
                </div>
                <div>
                  <strong>🔄 Status:</strong>{" "}
                  <span style={{ color: page?.status === 1 ? "#10b981" : "#ef4444" }}>
                    {page?.status === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Copyright Footer */}
          {!isEditing && (
            <div
              style={{
                padding: "20px 48px",
                borderTop: `1px solid ${t.border}`,
                backgroundColor: t.bg,
                fontSize: "13px",
                color: t.textSecondary,
                textAlign: "center",
              }}
            >
              © {new Date().getFullYear()} Zaptodoor. All rights reserved.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CancellationPolicy;